import { useState, useEffect, createContext, useContext } from 'react';
import { getAuthData, clearAuthData, saveAuthData } from '../services/authService';
import { cognitoService } from '../services/cognitoService';
import { oauthService } from '../services/oauthService';
import apiClient from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = () => {
      const authData = getAuthData();
      if (authData.accessToken && authData.userData) {
        setUser(authData.userData);
        setAuthMethod(authData.authMethod);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Traditional login
  const loginTraditional = async (email, password) => {
    try {
      const response = await apiClient.post('/user/login', {
        email,
        password
      });
      
      if (response.data.success) {
        const userData = response.data.data.user;
        setUser(userData);
        setAuthMethod('traditional');
        saveAuthData(response.data.data, 'traditional');
        return { success: true, user: userData };
      }
      
      throw new Error(response.data.message || 'Login failed');
    } catch (error) {
      throw error.response?.data || error;
    }
  };

  // Cognito login
  const loginCognito = async (email, password) => {
    try {
      const result = await cognitoService.login(email, password);
      if (result.success) {
        setUser(result.data.user);
        setAuthMethod('cognito');
        return result;
      }
      throw new Error(result.error || 'Cognito login failed');
    } catch (error) {
      throw error;
    }
  };

  // OAuth login
  const loginOAuth = () => {
    // Test OAuth parameters first
    oauthService.testOAuthParams();
    // Then initiate login
    oauthService.initiateLogin();
  };

  // Update auth state (for OAuth callbacks)
  const updateAuthState = (userData, method) => {
    setUser(userData);
    setAuthMethod(method);
  };

  // Handle OAuth authentication data (called from OAuth callback)
  const handleOAuthAuth = (authData) => {
    try {
      console.log('useAuth - Handling OAuth authentication data:', authData);
      
      // Extract data from OAuth response
      const { token: access_token, refreshToken: refresh_token, user: userData } = authData;
      
      // Store auth data using the proper service
      saveAuthData({ access_token, refresh_token, user: userData }, 'oauth');
      
      // Update auth context state
      setUser(userData);
      setAuthMethod('oauth');
      
      console.log('useAuth - OAuth authentication data stored and context updated');
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('useAuth - Error handling OAuth auth data:', error);
      throw error;
    }
  };

  // Register functions
  const registerTraditional = async (userData) => {
    try {
      const response = await apiClient.post('/user/register', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  };

  const registerCognito = async (userData) => {
    try {
      const result = await cognitoService.register(userData);
      if (result.success) {
        setUser(result.data.user);
        setAuthMethod('cognito');
        return result;
      }
      throw new Error(result.error || 'Cognito registration failed');
    } catch (error) {
      throw error;
    }
  };

  // Logout
  const logoutUser = async () => {
    try {
      const authMethod = localStorage.getItem('auth_method');
      
      // Call appropriate logout endpoint
      if (authMethod === 'cognito') {
        await apiClient.post('/auth/cognito/logout');
      } else if (authMethod === 'oauth') {
        await apiClient.post('/auth/oauth/logout');
      } else {
        await apiClient.post('/user/logout');
      }
      
      setUser(null);
      setAuthMethod(null);
      clearAuthData();
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local data even if server logout fails
      clearAuthData();
      setUser(null);
      setAuthMethod(null);
    }
  };

  const value = {
    user,
    authMethod,
    loading,
    isAuthenticated: !!user,
    setUser,
    setAuthMethod,
    updateAuthState,
    handleOAuthAuth,
    loginTraditional,
    loginCognito,
    loginOAuth,
    registerTraditional,
    registerCognito,
    logout: logoutUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};